import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { DayPickerRangeController } from "react-dates";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import {
	DATE_PRESET_CUSTOM,
	RELATIVE_DATE_PRESETS,
	computeRelativeDateRange,
} from "utils/relativeDatePresets";
import { useTranslation } from "react-i18next";

function toMoment(dateValue) {
	if (!dateValue) return null;
	if (moment.isMoment(dateValue)) return dateValue.clone();
	return moment(dateValue);
}

function formatRangeLabel(startDate, endDate, t) {
	if (!startDate || !endDate) return t("relativeDateNav.selectPeriod");

	const start = startDate.clone();
	const end = endDate.clone();
	const startDay = start.format("D");
	const endDay = end.format("D");
	const startMonth = start.format("MMM");
	const endMonth = end.format("MMM");
	const startYear = start.format("YYYY");
	const endYear = end.format("YYYY");
	const to = t("relativeDateNav.to");

	if (startYear !== endYear) {
		return `${startDay} ${startMonth} ${startYear} ${to} ${endDay} ${endMonth} ${endYear}`;
	}

	if (startMonth !== endMonth) {
		return `${startDay} ${startMonth} ${to} ${endDay} ${endMonth} ${endYear}`;
	}

	return `${startDay} ${to} ${endDay} ${endMonth} ${endYear}`;
}

const RelativeDateRangeNav = ({
	startDate,
	endDate,
	onDatesChange,
	activeDatePreset,
	onPresetChange,
	className,
	presets,
	onShift,
	maxDays,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [focusedInput, setFocusedInput] = useState("startDate");
	const containerRef = useRef(null);

	const start = useMemo(() => toMoment(startDate), [startDate]);
	const end = useMemo(() => toMoment(endDate), [endDate]);
	const label = useMemo(() => formatRangeLabel(start, end, t), [start, end, t]);

	useEffect(() => {
		if (!isOpen) return undefined;

		const handleClickOutside = (event) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				// eslint-disable-next-line no-console
				console.log("[RelativeDateRangeNav] click fuera -> cerrar");
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	const shiftRange = (direction) => {
		if (onShift) {
			onShift(direction);
			return;
		}
		if (!start || !end) return;

		const daysInRange = Math.max(
			1,
			end.clone().startOf("day").diff(start.clone().startOf("day"), "days") + 1
		);

		onDatesChange(
			{
				startDate: start.clone().add(direction * daysInRange, "days"),
				endDate: end.clone().add(direction * daysInRange, "days"),
			},
			DATE_PRESET_CUSTOM
		);
	};

	const handleToggle = () => {
		// eslint-disable-next-line no-console
		console.log("[RelativeDateRangeNav] toggle popover, prev isOpen=", isOpen);
		setFocusedInput("startDate");
		setIsOpen((prev) => !prev);
	};

	const handleDayPickerChange = ({
		startDate: nextStartDate,
		endDate: nextEndDate,
	}) => {
		// eslint-disable-next-line no-console
		console.log("[RelativeDateRangeNav] onDatesChange", {
			nextStartDate: nextStartDate?.format?.("YYYY-MM-DD"),
			nextEndDate: nextEndDate?.format?.("YYYY-MM-DD"),
		});
		onDatesChange(
			{ startDate: nextStartDate, endDate: nextEndDate },
			DATE_PRESET_CUSTOM
		);
	};

	const handleFocusChange = (nextFocusedInput) => {
		setFocusedInput(nextFocusedInput || "startDate");
	};

	const handlePresetClick = (presetId) => {
		// eslint-disable-next-line no-console
		console.log("[RelativeDateRangeNav] preset click", presetId);
		if (onPresetChange) {
			onPresetChange(presetId);
		} else {
			const range = computeRelativeDateRange(presetId, moment());
			if (range?.start && range?.end) {
				onDatesChange(
					{ startDate: range.start, endDate: range.end },
					presetId || DATE_PRESET_CUSTOM
				);
			}
		}
		setIsOpen(false);
	};

	return (
		<div
			className={`relative-date-range-nav ${className || ""}`.trim()}
			ref={containerRef}
		>
			<button
				type="button"
				className="btn-transparent calendar-left"
				onClick={() => shiftRange(-1)}
				aria-label={t("relativeDateNav.previousPeriod")}
			>
				<FiChevronLeft />
			</button>

			<button
				type="button"
				className="relative-date-range-nav__trigger"
				onClick={handleToggle}
				title={t("relativeDateNav.selectPeriod")}
			>
				{label}
			</button>

			<button
				type="button"
				className="btn-transparent calendar-right"
				onClick={() => shiftRange(1)}
				aria-label={t("relativeDateNav.nextPeriod")}
			>
				<FiChevronRight />
			</button>

			{isOpen && (
				<div className="relative-date-range-nav__popover">
					<div className="relative-date-range-nav__presets">
						{(presets || RELATIVE_DATE_PRESETS).map((preset) => {
							const isSelected = activeDatePreset === preset.id;
							return (
								<button
									key={preset.id}
									type="button"
									onClick={() => handlePresetClick(preset.id)}
									className={`relative-date-range-nav__preset-btn ${
										isSelected
											? "relative-date-range-nav__preset-btn--active"
											: ""
									}`}
								>
									{t(preset.label)}
								</button>
							);
						})}
					</div>

					<div className="relative-date-range-nav__calendar">
						<DayPickerRangeController
							startDate={start}
							endDate={end}
							onDatesChange={handleDayPickerChange}
							focusedInput={focusedInput || "startDate"}
							onFocusChange={handleFocusChange}
							numberOfMonths={2}
							isOutsideRange={(day) => {
								if (!maxDays || !start || focusedInput !== "endDate") return false;
								return (
									day
										.clone()
										.startOf("day")
										.diff(start.clone().startOf("day"), "days") >
									maxDays - 1
								);
							}}
							minimumNights={0}
							hideKeyboardShortcutsPanel
							noBorder
						/>
					</div>
				</div>
			)}
		</div>
	);
};

RelativeDateRangeNav.propTypes = {
	startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	onDatesChange: PropTypes.func.isRequired,
	activeDatePreset: PropTypes.string,
	onPresetChange: PropTypes.func,
	className: PropTypes.string,
	presets: PropTypes.array,
	onShift: PropTypes.func,
	maxDays: PropTypes.number,
};

RelativeDateRangeNav.defaultProps = {
	startDate: null,
	endDate: null,
	activeDatePreset: DATE_PRESET_CUSTOM,
	onPresetChange: null,
	className: "",
	presets: null,
	onShift: null,
	maxDays: null,
};

export default RelativeDateRangeNav;
